import React from 'react';

const ConnectionStatus = ({ 
  connected, 
  tokenType, 
  expiresAt, 
  environment = 'sandbox', 
  isActive = true,
  userInfo = null 
}) => {
  const getStatusColor = () => {
    if (!connected) return '#dc2626'; // red-600
    if (!isActive) return '#f59e0b'; // amber-500
    return '#16a34a'; // green-600
  };

  const getStatusText = () => {
    if (!connected) return 'Disconnected';
    if (!isActive) return 'Inactive';
    return 'Connected';
  };

  const getStatusIcon = () => {
    if (!connected) return '🔴';
    if (!isActive) return '🟡';
    return '🟢';
  };

  const isExpiringSoon = () => {
    if (!expiresAt) return false;
    const expiryTime = new Date(expiresAt);
    const now = new Date();
    const oneHour = 60 * 60 * 1000;
    return (expiryTime.getTime() - now.getTime()) < oneHour;
  };

  return (
    <div className="connection-status">
      <div className="flex items-center gap-3 mb-3">
        <div 
          className="traffic-light"
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: getStatusColor()
          }}
        />
        <div>
          <div className="font-semibold text-lg">
            DATEV API Status: {getStatusText()}
          </div>
          <div className="text-sm text-gray-600">
            Environment: <span className="font-mono capitalize">{environment}</span>
          </div>
        </div>
      </div>

      {connected && (
        <div className="connection-details space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Token Type:</span>
            <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
              {tokenType || 'unknown'}
            </span>
          </div>
          
          {expiresAt && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Expires:</span>
              <span className={`text-sm ${isExpiringSoon() ? 'text-orange-600 font-semibold' : ''}`}>
                {new Date(expiresAt).toLocaleString()}
                {isExpiringSoon() && ' ⚠️ Expiring Soon'}
              </span>
            </div>
          )}

          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-sm text-gray-800 mb-2">
              🔐 OAuth Scopes
            </h4>
            <div className="text-xs space-y-1">
              <div className="font-mono text-gray-700 break-all">
                openid profile email datev:accounting:clients accounting:documents datev:accounting:extf-files-import
              </div>
              <p className="mt-2 text-gray-600">
                These scopes enable access to DATEV accounting clients, document management, and EXTF file import functionality.
              </p>
            </div>
          </div>

          {userInfo && (
            <div className="user-info mt-3 p-3 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-sm text-blue-800 mb-2">
                👤 User Information
              </h4>
              <div className="text-sm space-y-1">
                {userInfo.full_name && (
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <span className="ml-2 font-semibold">{userInfo.full_name}</span>
                  </div>
                )}
                {userInfo.user_info?.email && (
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <span className="ml-2 font-mono text-xs">{userInfo.user_info.email}</span>
                  </div>
                )}
                {userInfo.user_info?.sub && (
                  <div>
                    <span className="text-gray-600">User ID:</span>
                    <span className="ml-2 font-mono text-xs">{userInfo.user_info.sub}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-sm text-gray-800 mb-2">
              🔗 DATEV App Management
            </h4>
            <div className="text-sm">
              <a 
                href="https://apps.datev.de/tokrevui" 
                target="_blank" 
                rel="noreferrer" 
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 underline"
              >
                <span>📱</span>
                Manage Connected Applications (Verbundene Anwendungen)
                <span className="text-xs">↗</span>
              </a>
              <p className="mt-2 text-gray-600 text-xs">
                View and manage all applications connected to your DATEV account, including this application.
              </p>
            </div>
          </div>
        </div>
      )}

      {!connected && (
        <div className="mt-3 p-3 bg-red-50 rounded-lg">
          <div className="text-red-800 text-sm">
            <strong>⚠️ Not Connected</strong>
            <p className="mt-1">
              Connect your DATEV account to enable document uploads and API functionality.
            </p>
          </div>
        </div>
      )}

      <style>{`
        .traffic-light {
          transition: all 0.3s ease;
        }
        
        .connection-status {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          background: white;
        }
        
        .connection-status.connected {
          border-color: #16a34a;
          background: #f0fdf4;
        }
        
        .connection-status.disconnected {
          border-color: #dc2626;
          background: #fef2f2;
        }
        
        .connection-status.inactive {
          border-color: #f59e0b;
          background: #fffbeb;
        }
      `}</style>
    </div>
  );
};

export default ConnectionStatus;